import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import {
  ColumnDef,
  ColumnFiltersState,
  flexRender,
  FilterFn,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  PaginationState,
  Row,
  RowSelectionState,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import cs from 'classnames';

import CenteredSpinner from 'features/ui/CenteredSpinner';
import Skeleton from 'shared/components/atoms/Skeleton';
import Tooltip from 'shared/components/atoms/Tooltip';
import { Box } from 'shared/components/display';
import Checkbox from 'shared/components/atoms/Checkbox';

import { GENERIC_COPY } from 'shared/config/copy';
import { noOp } from 'shared/defaults';
import { useQuery } from 'shared/hooks/useQuery';

import ColumnFilterPopover from './ColumnFilterPopover';
import PaginationControls from './PaginationControls';
import SortableHeader from './SortableHeader';
import { CLASSNAMES, MAIN_CLASS } from './styles';
import styles from './styles';
import type { CheckboxFilterValue, TextFilterValue } from './types';

export interface BrkfstTableProps<T> {
  data: T[];
  columns: ColumnDef<T, any>[];
  pagination?: boolean;
  onRowClick?: (el?: any) => void;
  noResultsMessage?: string;
  loading?: boolean;
  loadingNewRow?: boolean;
  dataCy?: string;

  // ---- Row selection (opt-in) ---------------------------------------------
  /** Enable per-row checkbox selection. A selection column is prepended automatically. */
  enableRowSelection?: boolean;
  /** Controlled selection state, keyed by row id. */
  rowSelection?: RowSelectionState;
  /** Called when the selection state changes. */
  onRowSelectionChange?: (selection: RowSelectionState) => void;
  /**
   * Stable row id accessor. Required when `enableRowSelection` is true so selection
   * survives re-renders / data refreshes. Defaults to row index, which would lose
   * selection after a re-fetch.
   */
  getRowId?: (row: T, index: number) => string;
  /**
   * Optional predicate to disable selection on specific rows (e.g. already-stamped
   * approvals). Returns true if the row CAN be selected.
   */
  canRowBeSelected?: (row: T) => boolean;

  // ---- Sorting (opt-in) ---------------------------------------------------
  /** Enable click-to-sort on all columns (per-column opt-out via column meta). */
  enableSorting?: boolean;

  // ---- Column filters (opt-in) --------------------------------------------
  /** Enable per-column filter popovers (per-column opt-in via column meta filterType). */
  enableColumnFilters?: boolean;
}

// ---------------------------------------------------------------------------
// Filter functions for the contains / does-not-contain / checkbox-list modes.
// ---------------------------------------------------------------------------

/**
 * Text filter: filterValue is { mode: 'contains' | 'notContains', value: string }.
 * Compares case-insensitive. Falls back to the column's accessor value when the
 * meta.filterAccessor is not provided.
 */
const textFilter: FilterFn<any> = (row, columnId, filterValue) => {
  const v = filterValue as TextFilterValue | undefined;
  if (!v || !v.value) return true;
  const meta = row.getAllCells().find((c) => c.column.id === columnId)?.column.columnDef.meta;
  const raw = meta?.filterAccessor ? meta.filterAccessor(row.original) : row.getValue(columnId);
  const haystack = raw == null ? '' : String(raw).toLowerCase();
  const needle = v.value.toLowerCase();
  const contains = haystack.includes(needle);
  return v.mode === 'contains' ? contains : !contains;
};

/**
 * Checkbox filter: filterValue is string[] (selected values). Empty array means
 * "no filter" — every row passes.
 */
const checkboxFilter: FilterFn<any> = (row, columnId, filterValue) => {
  const selected = filterValue as CheckboxFilterValue | undefined;
  if (!selected || selected.length === 0) return true;
  const meta = row.getAllCells().find((c) => c.column.id === columnId)?.column.columnDef.meta;
  const raw = meta?.filterAccessor ? meta.filterAccessor(row.original) : row.getValue(columnId);
  return selected.includes(raw == null ? '' : String(raw));
};

const SELECTION_COLUMN_ID = '__select__';

const BrkfstTable = <T extends {}>({
  data,
  columns,
  pagination = false,
  onRowClick,
  noResultsMessage = GENERIC_COPY.PLACEHOLDER_NO_DATA,
  loading = false,
  loadingNewRow = false,
  dataCy,
  enableRowSelection = false,
  rowSelection,
  onRowSelectionChange,
  getRowId,
  canRowBeSelected,
  enableSorting = false,
  enableColumnFilters = false,
}: BrkfstTableProps<T>) => {
  const { getQuery, setQuery } = useQuery(false);
  const query = getQuery();
  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: query.pageIndex > 0 ? query.pageIndex : 1,
    pageSize: query.pageSize | 20,
  });
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const paginationData = React.useMemo(
    () => ({
      pageIndex,
      pageSize,
    }),
    [pageIndex, pageSize],
  );

  useEffect(() => {
    if (pagination) setQuery({ ...paginationData, pageIndex: paginationData.pageIndex + 1 });
  }, [pageIndex, pageSize]);

  // Auto-prepend a checkbox column when row selection is enabled. The header
  // checkbox toggles all visible rows that pass `canRowBeSelected`.
  const augmentedColumns = useMemo<ColumnDef<T, any>[]>(() => {
    if (!enableRowSelection) return columns;
    const selectionColumn: ColumnDef<T, any> = {
      id: SELECTION_COLUMN_ID,
      enableSorting: false,
      enableColumnFilter: false,
      header: ({ table }) => {
        const selectable = table.getRowModel().rows.filter((r) =>
          canRowBeSelected ? canRowBeSelected(r.original) : true,
        );
        const allSelected =
          selectable.length > 0 && selectable.every((r) => r.getIsSelected());
        const someSelected = selectable.some((r) => r.getIsSelected());
        return (
          <Checkbox
            name={`${SELECTION_COLUMN_ID}-header`}
            checked={allSelected}
            onChange={(e) => {
              const next = e.target.checked;
              selectable.forEach((r) => r.toggleSelected(next));
            }}
            // @ts-ignore — Checkbox forwards refs but doesn't expose indeterminate;
            // visually the header still shows checked when partial, which is acceptable.
            data-indeterminate={someSelected && !allSelected ? 'true' : undefined}
          />
        );
      },
      cell: ({ row }) => {
        const selectable = canRowBeSelected ? canRowBeSelected(row.original) : true;
        return (
          <Checkbox
            name={`${SELECTION_COLUMN_ID}-${row.id}`}
            checked={row.getIsSelected()}
            disabled={!selectable}
            onChange={() => row.toggleSelected()}
          />
        );
      },
      meta: { width: '40px' },
    };
    return [selectionColumn, ...columns];
  }, [columns, enableRowSelection, canRowBeSelected]);

  const table = useReactTable({
    data,
    columns: augmentedColumns,
    state: {
      ...(pagination ? { pagination: paginationData } : {}),
      ...(enableRowSelection ? { rowSelection: rowSelection ?? {} } : {}),
      ...(enableSorting ? { sorting } : {}),
      ...(enableColumnFilters ? { columnFilters } : {}),
    },
    ...(getRowId ? { getRowId } : {}),
    ...(enableRowSelection
      ? {
          enableRowSelection: canRowBeSelected
            ? (row: Row<T>) => canRowBeSelected(row.original)
            : true,
          onRowSelectionChange: (updater) => {
            const next = typeof updater === 'function' ? updater(rowSelection ?? {}) : updater;
            onRowSelectionChange?.(next);
          },
        }
      : {}),
    ...(enableSorting
      ? {
          enableSorting: true,
          onSortingChange: setSorting,
          getSortedRowModel: getSortedRowModel(),
        }
      : { enableSorting: false }),
    ...(enableColumnFilters
      ? {
          onColumnFiltersChange: setColumnFilters,
          filterFns: { textFilter, checkboxFilter },
          // Per-column filterFn is set below via columnDef.meta inference
        }
      : {}),
    ...(pagination
      ? {
          initialState: {
            pagination: {
              pageIndex: query.pageIndex,
              pageSize: getQuery().pageSize,
            },
          },
          onPaginationChange: setPagination,
          getPaginationRowModel: getPaginationRowModel(),
        }
      : {}),
    ...(enableColumnFilters || pagination
      ? { getFilteredRowModel: getFilteredRowModel() }
      : {}),
    getCoreRowModel: getCoreRowModel(),
  });

  // Wire per-column filterFns based on column meta. We can't set filterFn through
  // the table options because the columns are defined externally; instead we mutate
  // the column instance after construction. This is a one-time effect.
  useEffect(() => {
    if (!enableColumnFilters) return;
    table.getAllColumns().forEach((col) => {
      const type = col.columnDef.meta?.filterType;
      if (!type) return;
      // @ts-ignore — runtime assignment of filterFn is supported but not in types
      col.columnDef.filterFn = type === 'text' ? textFilter : checkboxFilter;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enableColumnFilters, columns]);

  const onStartClick = () => table.setPageIndex(0);
  const onEndClick = () => table.setPageIndex(table.getPageCount() - 1);
  const onPreviousClick = () => table.previousPage();
  const onNextClick = () => table.nextPage();
  const handleRowClick = (row: Row<T>, rowIndex) => (onRowClick ? onRowClick({ ...row.original, rowIndex }) : noOp);

  return (
    <Box className={MAIN_CLASS} css={styles}>
      <table className={CLASSNAMES.table} data-cy={dataCy}>
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                const meta = header.column.columnDef.meta;
                const canFilter =
                  enableColumnFilters && !!meta?.filterType && header.column.id !== SELECTION_COLUMN_ID;
                const canSort =
                  enableSorting &&
                  header.column.id !== SELECTION_COLUMN_ID &&
                  header.column.getCanSort();
                const headerNode = header.isPlaceholder
                  ? null
                  : flexRender(header.column.columnDef.header, header.getContext());
                return (
                  <th
                    className={cs(CLASSNAMES.tableHeader, meta?.headerClassName)}
                    key={header.id}
                    style={{
                      width: meta?.width,
                      paddingLeft: meta?.paddingLeft,
                    }}
                  >
                    <Box className={cs('brkfst-table__header-content')}>
                      {canSort ? (
                        <SortableHeader header={header}>{headerNode}</SortableHeader>
                      ) : (
                        headerNode
                      )}
                      {canFilter && <ColumnFilterPopover column={header.column} table={table} />}
                    </Box>
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody className={CLASSNAMES.tableBody}>
          {loading || data?.length <= 0 ? (
            <tr>
              <td className={CLASSNAMES.noResultsMessage} colSpan={table.getAllColumns().length}>
                {loading ? <CenteredSpinner /> : noResultsMessage}
              </td>
            </tr>
          ) : (
            <>
              {table.getRowModel().rows.map((row, index) => (
                <tr
                  // @ts-ignore
                  height="35px"
                  key={row.id}
                  className={cs(CLASSNAMES.tableRow, {
                    [CLASSNAMES.tableRowClickable]: !!onRowClick,
                    'brkfst-table__row--selected': row.getIsSelected(),
                  })}
                  onClick={() => handleRowClick(row, index)}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td
                      className={cs(CLASSNAMES.tableCell, cell.column.columnDef.meta?.cellClassName)}
                      key={cell.id}
                      style={{
                        width: cell.column.columnDef.meta?.width,
                        paddingLeft: cell.column.columnDef.meta?.paddingLeft,
                      }}
                    >
                      {cell.column.columnDef.meta?.addTooltip ? (
                        <Tooltip
                          content={
                            <Box className={CLASSNAMES.tooltipContentWrapper}>
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </Box>
                          }
                          as="div"
                          arrow
                          placement={'left'}
                          touch={['hold', 100]}
                          className={CLASSNAMES.cellContent}
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </Tooltip>
                      ) : (
                        <Box className={CLASSNAMES.cellContent}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </Box>
                      )}
                    </td>
                  ))}
                </tr>
              ))}
              {loadingNewRow && (
                // @ts-ignore
                <tr height="35px" key="loading-row" className={cs(CLASSNAMES.tableRow)}>
                  <td colSpan={table.getAllColumns().length}>
                    <Skeleton />
                  </td>
                </tr>
              )}
            </>
          )}
        </tbody>
        <tfoot>
          {table.getFooterGroups().map((footerGroup) => (
            <tr key={footerGroup.id}>
              {footerGroup.headers.map((header) => (
                <th key={header.id}>
                  {header.isPlaceholder ? null : flexRender(header.column.columnDef.footer, header.getContext())}
                </th>
              ))}
            </tr>
          ))}
        </tfoot>
      </table>
      {pagination ? (
        <PaginationControls
          pageIndex={table.getState().pagination.pageIndex + 1}
          pageCount={table.getPageCount()}
          onStartClick={onStartClick}
          onEndClick={onEndClick}
          onPreviousClick={onPreviousClick}
          onNextClick={onNextClick}
          disableNext={!table.getCanNextPage()}
          disablePrevious={!table.getCanPreviousPage()}
        />
      ) : (
        <></>
      )}
    </Box>
  );
};

export default BrkfstTable;
